import type { SheetScale } from '@otto/onscreen-engine';
import { COLORS, mono } from './styles.ts';
import type { CalibStatus, Tool } from './helpers.ts';

interface Props {
  tool: Tool;
  onToolChange: (t: Tool) => void;
  calibStatus: CalibStatus;
  scale: SheetScale | null;
  inProgressCount: number;
  onFinish: () => void;
  onCancel: () => void;
}

const TOOLS: Array<{ id: Tool; label: string; hint: string; needsScale: boolean }> = [
  { id: 'pointer', label: 'Pointer', hint: 'Select / inspect items', needsScale: false },
  { id: 'calibrate', label: 'Calibrate', hint: 'Click two points on a known dimension', needsScale: false },
  { id: 'linear', label: 'Linear', hint: 'Click points; double-click or Enter to finish', needsScale: true },
  { id: 'polygon', label: 'Polygon', hint: 'Click points; close to finish', needsScale: true },
  { id: 'count', label: 'Count', hint: 'Click to drop count markers', needsScale: false },
];

export function ToolBar({
  tool,
  onToolChange,
  calibStatus,
  scale,
  inProgressCount,
  onFinish,
  onCancel,
}: Props) {
  const message = buildMessage(tool, calibStatus, scale, inProgressCount);
  const tone = messageTone(tool, calibStatus, scale);
  const canFinish = canFinishCurrent(tool, inProgressCount);
  const showCancel = inProgressCount > 0 || calibStatus !== 'idle';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        marginBottom: 12,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ ...mono(11, COLORS.textDim), letterSpacing: '0.1em' }}>TOOLS</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {TOOLS.map((t) => {
          const active = tool === t.id;
          const disabled = t.needsScale && !scale;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => !disabled && onToolChange(t.id)}
              disabled={disabled}
              title={disabled ? 'Calibrate scale first' : t.hint}
              style={{
                background: active ? COLORS.accentSoft : 'transparent',
                border: `1px solid ${active ? COLORS.accentBorder : COLORS.border}`,
                color: disabled ? COLORS.textDim : active ? COLORS.accent : COLORS.textMuted,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: '0.08em',
                padding: '6px 10px',
                borderRadius: 5,
                textTransform: 'uppercase',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <span style={{ fontSize: 13, color: tone, flex: 1, minWidth: 200 }}>{message}</span>
      {canFinish && (
        <button
          type="button"
          onClick={onFinish}
          style={{
            background: COLORS.accentSoft,
            border: `1px solid ${COLORS.accentBorder}`,
            color: COLORS.accent,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            letterSpacing: '0.1em',
            padding: '6px 12px',
            borderRadius: 5,
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Finish
        </button>
      )}
      {showCancel && (
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: 'transparent',
            border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            letterSpacing: '0.1em',
            padding: '6px 12px',
            borderRadius: 5,
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
}

function buildMessage(
  tool: Tool,
  calibStatus: CalibStatus,
  scale: SheetScale | null,
  inProgressCount: number,
): string {
  if (tool === 'calibrate') {
    if (calibStatus === 'awaiting_first') return 'Click the first endpoint of a known dimension.';
    if (calibStatus === 'awaiting_second') return 'Click the second endpoint.';
    if (calibStatus === 'awaiting_distance') return 'Enter the real-world distance.';
    return scale
      ? `Calibrated · ${scale.pixelsPerFoot.toFixed(2)} px/ft. Click two points to recalibrate.`
      : 'Click two points on a known dimension to calibrate.';
  }
  if (tool === 'linear') {
    if (inProgressCount === 0) return 'Click to start a linear path.';
    if (inProgressCount === 1) return 'Click the next point. Double-click or press Enter to finish.';
    return `${inProgressCount} points. Double-click or press Enter to finish, Esc to cancel.`;
  }
  if (tool === 'polygon') {
    if (inProgressCount === 0) return 'Click to start a polygon.';
    if (inProgressCount < 3) return `${inProgressCount} point${inProgressCount === 1 ? '' : 's'}. Need at least 3.`;
    return `${inProgressCount} points. Click first point or press Enter to close.`;
  }
  if (tool === 'count') return 'Click to drop count markers.';
  return scale
    ? `Calibrated · ${scale.pixelsPerFoot.toFixed(2)} px/ft (${scale.source}).`
    : 'No scale set. Pick the Calibrate tool.';
}

function messageTone(tool: Tool, calibStatus: CalibStatus, scale: SheetScale | null): string {
  if (tool === 'calibrate' && calibStatus !== 'idle') return COLORS.purple;
  if ((tool === 'linear' || tool === 'polygon' || tool === 'count') && scale) return COLORS.purple;
  return scale ? COLORS.green : COLORS.textMuted;
}

function canFinishCurrent(tool: Tool, inProgressCount: number): boolean {
  if (tool === 'linear') return inProgressCount >= 2;
  if (tool === 'polygon') return inProgressCount >= 3;
  return false;
}
